import { useState, useCallback, useMemo } from "react";
import { useAutumnSubscription } from "@/hooks/useAutumnSubscription";

export type SubscriptionState = "active" | "grace" | "expired" | "none";

const GRACE_PERIOD_DAYS = 7;

export function useSubscriptionGate() {
  const [showModal, setShowModal] = useState(false);
  const { hasAccess, hasLegacyLifetimeAccess, isInTrial, getDaysRemaining, getTrialEndsAt, isCustomerLoaded, isAccessLoaded } = useAutumnSubscription();

  const hasValidAccess = useCallback(() => {
    return (
      hasLegacyLifetimeAccess() ||
      hasAccess("monthly") ||
      hasAccess("yearly") ||
      hasAccess("lifetime")
    );
  }, [hasAccess, hasLegacyLifetimeAccess]);

  const subscriptionState: SubscriptionState = useMemo(() => {
    // Active: user has a valid subscription or is in trial
    if (hasValidAccess()) return "active";

    // Check if trial has ended and compute grace window
    const trialEndsAt = getTrialEndsAt();
    if (trialEndsAt) {
      const trialEnd = new Date(trialEndsAt);
      const now = new Date();
      const gracePeriodEnd = new Date(trialEnd.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);

      if (now <= gracePeriodEnd) return "grace";
      return "expired";
    }

    // No trial data at all — user never started a trial
    return "none";
  }, [hasValidAccess, getTrialEndsAt]);

  const graceDaysRemaining = useMemo(() => {
    if (subscriptionState !== "grace") return 0;
    const trialEndsAt = getTrialEndsAt();
    if (!trialEndsAt) return 0;
    const trialEnd = new Date(trialEndsAt);
    const gracePeriodEnd = new Date(trialEnd.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
    const now = new Date();
    return Math.max(0, Math.ceil((gracePeriodEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
  }, [subscriptionState, getTrialEndsAt]);

  // Backward-compatible helpers
  const isTrialExpired = useCallback(() => {
    return subscriptionState === "grace" || subscriptionState === "expired";
  }, [subscriptionState]);

  const requireSubscription = useCallback((callback: () => void) => {
    if (hasValidAccess()) {
      callback();
    } else {
      setShowModal(true);
    }
  }, [hasValidAccess]);

  return {
    showModal,
    setShowModal,
    subscriptionState,
    graceDaysRemaining,
    hasValidAccess,
    isTrialExpired,
    requireSubscription,
    isInTrial,
    getDaysRemaining,
    isCustomerLoaded,
    isAccessLoaded,
  };
}

export interface OwnerSubscriptionInfo {
  state: SubscriptionState;
  graceDaysRemaining: number;
}

/**
 * Determine subscription state for a flow owner by their user_id.
 * Used by public flow pages to check if the owner's flows should be active.
 * This is a standalone async function (not a hook) for use in useEffect.
 *
 * Uses a SECURITY DEFINER RPC function so that unauthenticated visitors
 * can still verify the owner's subscription status (profiles RLS blocks
 * direct reads for non-owners).
 */
export async function getOwnerSubscriptionState(
  userId: string,
  supabase: any
): Promise<OwnerSubscriptionInfo> {
  // Try the RPC function first (works for unauthenticated users)
  const { data, error } = await supabase.rpc("get_owner_subscription_info", {
    p_user_id: userId,
  });

  if (!error && data) {
    return {
      state: (data.state as SubscriptionState) || "expired",
      graceDaysRemaining: data.grace_days_remaining ?? 0,
    };
  }

  // Fallback: direct profile query (works for authenticated owner)
  const { data: profile, error: profileError } = await supabase
    .from("profiles")
    .select("has_lifetime_access, is_in_trial, trial_ends_at, subscription_active_until")
    .eq("id", userId)
    .single();

  if (profileError || !profile) return { state: "expired", graceDaysRemaining: 0 };

  if (profile.has_lifetime_access) return { state: "active", graceDaysRemaining: 0 };

  // Check Autumn subscription synced from client
  if (profile.subscription_active_until) {
    const subEnd = new Date(profile.subscription_active_until);
    const now = new Date();
    if (subEnd > now) return { state: "active", graceDaysRemaining: 0 };

    const gracePeriodEnd = new Date(subEnd.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
    if (now <= gracePeriodEnd) {
      const daysLeft = Math.max(0, Math.ceil((gracePeriodEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
      return { state: "grace", graceDaysRemaining: daysLeft };
    }
  }

  if (profile.is_in_trial && profile.trial_ends_at) {
    const trialEnd = new Date(profile.trial_ends_at);
    if (trialEnd > new Date()) return { state: "active", graceDaysRemaining: 0 };
  }

  if (profile.trial_ends_at) {
    const trialEnd = new Date(profile.trial_ends_at);
    const gracePeriodEnd = new Date(trialEnd.getTime() + GRACE_PERIOD_DAYS * 24 * 60 * 60 * 1000);
    const now = new Date();
    if (now <= gracePeriodEnd) {
      const daysLeft = Math.max(0, Math.ceil((gracePeriodEnd.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
      return { state: "grace", graceDaysRemaining: daysLeft };
    }
    return { state: "expired", graceDaysRemaining: 0 };
  }

  return { state: "none", graceDaysRemaining: 0 };
}
